import { SHOW_REPLIK_UI } from "./replikFeature";
import { SHOW_ROW_SPAN_UI } from "./rowSpanFeature";

export type HelpSectionId =
  | "overview"
  | "manus"
  | "scenes"
  | "views"
  | "status"
  | "shortcuts";

export type HelpSection = {
  id: HelpSectionId;
  title: string;
  paragraphs: string[];
};

export type HelpTip = {
  id: string;
  text: string;
  sectionId: HelpSectionId;
};

export const HELP_SECTIONS: HelpSection[] = [
  {
    id: "overview",
    title: "Overblik",
    paragraphs: [
      "På produktionsforsiden opretter du sæsoner og episoder. Klik på et episodenavn for at åbne den. Træk i ⋮⋮, eller brug Flyt op/Flyt ned under Håndtér episode, for at ændre rækkefølgen. Håndtér episode kan også omdøbe, duplikere, gemme en sikkerhedskopi af hele episodemanuset (inkl. noter) og slette (til papirkurven). Indsæt fra fil… genskaber en episode fra en sådan sikkerhedskopi. Tilrettelæggermanus åbner episoden med dine egne sceneversioner oven på programmanus — kun scener, du har oprettet version af, gemmes privat; resten vises fra programmet. Aflever sender en kopi til Afleverede scener og låser scenen i dit view. Lås op for at redigere den afleverede version (valgfrit arkivkopi først). Når en aflevering er indsat i programmanus, forsvinder den fra listen, og du skal aflevere på ny efter yderligere redigering. Papirkurv, arkiv og afleveringer ligger nederst. Papirkurv og arkiv slettes automatisk efter 30 dage. Brug ← Produktionsforside i toppanelet for at vende tilbage.",
      "Eksportér/Udskriv åbner Forbered udskrift. For manus og noter er standardvalget den åbne scene — skift til udvalgte scener eller hele episoden efter behov. Gentag sceneoverskrift på nye sider tænder du dér. Udskriv… åbner browserens udskriftsdialog; Eksportér som … gemmer PDF, RTF eller Word. En sikkerhedskopi (.smepisode.json) er den fulde episodetekst til genskabelse — ikke det samme som PDF/RTF/Word. Scene- og episodenumre (fortløbende eller brugerdefinerede) og Vis episodenumre sættes i Indstillinger. Du kan sende et link til forsiden, en episodefane, en scene eller en notekolonne — andre med adgang lander det samme sted.",
      "Hvis internettet ryger, viser tjenesten en advarsel, fordi den ikke kan gemme. Luk ikke fanen. Vent med at skrive mere, eller eksporter en kopi. Når nettet er tilbage, gemmes det, der stadig ligger i fanen, automatisk.",
    ],
  },
  {
    id: "manus",
    title: "Manus",
    paragraphs: [
      "Dobbelt Return opretter et nyt tekstpar i manus og i oplåste billed-/lydceller på Noter (kan slås fra i Indstillinger). Første Return i en tom celle er et linjeskift. Træk i grebet øverst til venstre for at flytte et par.",
      `${SHOW_ROW_SPAN_UI ? "Tænd Højde øverst for at trække i underkanten af billed- eller lydsiden og dække flere rækker. " : ""}Fed, kursiv, tekstfarve og de øvrige tekstknapper ligger i topmenuen, når en celle er aktiv. Tekstfarve vælges fra en fast palet. Plus-knappen sidder nederst ude på siden af den aktive celle — til venstre i venstre kolonne og til højre i højre kolonne. I øverste række vises også et plus oven over cellen, så du kan indsætte en række først i scenen.`,
      "Klik i en celle for at skrive. Hold Shift nede og klik for at markere hele cellen, så formatknapper og Ctrl/⌘+B gælder for al teksten. Første klik markerer cellen (Indstillinger) gør, at et almindeligt klik også markerer; andet klik i en celle med tekst sætter cursoren. I en tom celle kan du skrive med det samme.",
      "Musen markerer tekst i cellen, når du er i skrivetilstand. Tænd Markér celler for at vælge flere celler (kopier eller slet).",
      "Under Egne streger: klik på den vandrette linje under rækken for at vise eller skjule den på udskrift. På Noter tændes og slukkes linjerne på samme måde.",
    ],
  },
  {
    id: "scenes",
    title: "Scener",
    paragraphs: [
      "Tilføj scene ligger øverst på hver fane. På Sceneliste åbner et klik på scenetitlen manus. Ret numre, varighed og rækkefølge (træk i grebet) direkte. Blyanten øverst til højre slår redigering af titlerne til. På Rundown kan du også trække i grebet. Ved fortløbende numre spørges du om omnummerering.",
      "Scenenumre sættes i Indstillinger. Klik på nummeret for at rette det — episoden skifter da til brugerdefinerede numre. De kan indeholde bogstaver, punktummer og skråstreg (fx 12a, 2.1 eller 3 / 4).",
      "Episodenumre sættes på samme måde. Vis episodenumre i Indstillinger sætter «Episode X: » foran titlen. Retter du et nummer, skifter produktionen til brugerdefinerede episodenumre.",
      "Højreklik på en sceneoverskrift for baggrundsfarve, duplikering, kopiering eller flytning til en anden episode, arkivering (kopi i arkivet, op til tre versioner — du kan give hver forekomst et valgfrit navn), opdeling, fletning eller slet (til papirkurven). Arkiverede versioner… åbner en liste, hvor du kan læse hele indholdet og gendanne en version (erstat den aktuelle scene eller indsæt den ved siden af). Ved fletning vælger du navn — og ved brugerdefinerede numre også nummer. Opdel scene findes kun i manus: vælg først punktet, klik derefter på linjen mellem de to rækker. Mens du vælger linjen, kan der ikke skrives i manus.",
    ],
  },
  {
    id: "views",
    title: "Visninger",
    paragraphs: [
      "Fanerne skifter mellem Sceneliste, Rundown, Manus, Noter, Creditliste, Programtekst og Status. Træk i en fane for at ændre rækkefølgen — den gemmes hos dig. Højreklik på en fane for «Kopiér link til fane», på en sceneoverskrift for «Kopiér link til scene», eller på en notekolonne for link til den kolonne i den åbne scene. På produktionsforsiden kopierer du et episodelink under Håndtér episode.",
      "På Status og Creditliste klikker du på en kasse for at udvide den. Creditlisten har Vis samlet creditliste og Udskriv samlet creditliste.",
      "På Programtekst kan du tilføje én eller flere navngivne tekster til episoden. På Noter: skriv en note for hele scenen over cue-rækkerne (følger den valgte notekolonne). Lås op for at skrive i billed- og lydfelterne; ellers er de kun til visning. Når manus er låst op, flytter Tab og piletaster mellem alle celler; er manus låst, kun mellem noterækkerne. Omdøb notekolonnens overskrift, og skift mellem flere kolonner. Shift-klik markerer feltet som i manus. Vandrette linjer tændes som i manus.",
    ],
  },
  {
    id: "status",
    title: "Status",
    paragraphs: [
      "Udvid en scene for at sætte kryds og trække punkterne rundt. Rediger-knappen ændrer den scenes punkter. Standardpunkter tilføjer eller fjerner punkter på alle scener uden at overskrive deres øvrige liste. «Hele episoden» kan klappes sammen som scenerne.",
      "Forbered udskrift (Eksportér/Udskriv): vælg sammenklappet overblik for alle scener, eller udvidet visning med alle trin.",
    ],
  },
  {
    id: "shortcuts",
    title: "Genveje",
    paragraphs: [
      "Ctrl/⌘+B fed, Ctrl/⌘+I kursiv, Ctrl/⌘+U understreget. Shift-klik i en celle (eller Første klik markerer cellen) gør, at genvejene gælder for hele cellen. Tab og piletaster flytter mellem celler i manus og på Noter.",
      ...(SHOW_REPLIK_UI
        ? [
            "Ctrl/⌘+1–9 tildeler medvirkende til den aktive celle i manus.",
          ]
        : []),
    ],
  },
];

export const HELP_TIPS: HelpTip[] = [
  {
    id: "double-return",
    text: "Dobbelt Return i manus (og i oplåste billed-/lydceller på Noter) opretter et nyt tekstpar (kan slås fra i Indstillinger).",
    sectionId: "manus",
  },
  ...(SHOW_ROW_SPAN_UI
    ? [
        {
          id: "height",
          text: "Tænd Højde, og træk i underkanten for at dække flere rækker.",
          sectionId: "manus" as const,
        },
      ]
    : []),
  {
    id: "offline-save",
    text: "Hvis nettet ryger, vises en advarsel. Luk ikke fanen, og eksporter gerne en kopi. Når nettet er tilbage, gemmes automatisk.",
    sectionId: "overview",
  },
  {
    id: "share-links",
    text: "Kopiér browserens adresse, eller højreklik på en fane, sceneoverskrift eller notekolonne for at sende et link til andre med adgang.",
    sectionId: "views",
  },
  {
    id: "click-selects-cell",
    text: "Klik i manus for at skrive. Shift-klik markerer cellen, så format gælder for al teksten. Første klik markerer cellen kan slås til i Indstillinger.",
    sectionId: "manus",
  },
  {
    id: "cells",
    text: "Tænd Markér celler for at markere flere celler ad gangen.",
    sectionId: "manus",
  },
  ...(SHOW_REPLIK_UI
    ? [
        {
          id: "cast-keys",
          text: "Ctrl/⌘+1–9 tildeler medvirkende til den aktive celle.",
          sectionId: "shortcuts" as const,
        },
      ]
    : []),
  {
    id: "scene-drag",
    text: "På Sceneliste: klik på titlen for at åbne manus. Blyanten øverst til højre lader dig rette titlerne.",
    sectionId: "scenes",
  },
  {
    id: "dashboard",
    text: "Klik på et episodenavn for at åbne det. Tilrettelæggermanus er din private overlay; Aflever lægger en kopi under Afleverede scener.",
    sectionId: "overview",
  },
  {
    id: "director",
    text: "I tilrettelæggermanus: Opret min version, Aflever (låser), Lås op for at rette den afleverede kopi. Indsat i programmanus → aflever igen efter redigering.",
    sectionId: "overview",
  },
  {
    id: "status-edit",
    text: "Udvid en scene på Status for at sætte kryds og trække punkterne rundt. Rediger ændrer den scenes punkter.",
    sectionId: "status",
  },
];

const TIP_INDEX_KEY = "minproduktion.helpTipIndex";
const LEGACY_TIP_INDEX_KEY = "supermanus.helpTipIndex";

export function loadHelpTipIndex(): number {
  try {
    const raw =
      localStorage.getItem(TIP_INDEX_KEY) ??
      localStorage.getItem(LEGACY_TIP_INDEX_KEY);
    const n = raw ? Number.parseInt(raw, 10) : 0;
    if (!Number.isFinite(n) || n < 0) return 0;
    return n % HELP_TIPS.length;
  } catch {
    return 0;
  }
}

export function saveHelpTipIndex(index: number): void {
  try {
    localStorage.setItem(TIP_INDEX_KEY, String(index % HELP_TIPS.length));
  } catch {
    /* ignore */
  }
}
